import "@total-typescript/ts-reset";
import { StrictMode } from "react";
import config from "~/temp/config";
import i18ninit from "~/foundation/Dictionary/i18n";
import { HelmetProvider } from "react-helmet-async";
import { preloadReady } from "~/foundation/ReactLoadable";
import { SitecoreConfigProvider } from "./foundation/Jss/SitecoreConfigContext";
import { hydrateRoot } from "react-dom/client";
import { BrowserRouter } from "react-router";
import AppRoot from "./project/AramcoCom/AppRoot";

let initLanguage = config.defaultLanguage;

/*
	SSR Data
	If we're running in a server-side rendering scenario,
	the server will provide JSON in the #__JSS_STATE__ element
	for us to acquire the initial state to run with on the client.

	This enables us to skip a network request to load up the layout data.
	We are emitting a quiescent script with JSON so that we can take advantage
	of JSON.parse()'s speed advantage over parsing full JS, and enable
	working without needing `unsafe-inline` in Content Security Policies.

	SSR is initiated from /server/server.js.
*/
let __JSS_STATE__: any = null; // eslint-disable-line
const ssrRawJson = document.getElementById("__JSS_STATE__");
if (ssrRawJson) {
	__JSS_STATE__ = JSON.parse(ssrRawJson.innerHTML);
}
if (__JSS_STATE__) {
	// set i18n language SSR state language instead of static config default language
	initLanguage = __JSS_STATE__.sitecore.context.language;
}


// used by GTM
window.getTrackingData = (element: HTMLElement, index: number) => {
	const dataTracking = element.dataset?.trackingData;
	if (!dataTracking) {
		return;
	}

	const json = JSON.parse(dataTracking) as any; // eslint-disable-line
	const keys = Object.keys(json);
	const name = keys[index];
	const value = json[name];

	return {
		name,
		value
	};
}

const rootElement = document.getElementById("root");
if (!rootElement) {
	throw "Could not find root element";
}

const siteName = rootElement.dataset.siteName || config.sitecoreSiteName;


/*
	App Rendering
*/
// initialize the dictionary, then render the app
// note: if not making a multlingual app, the dictionary init can be removed.

i18ninit(siteName, initLanguage, undefined)
	.then(() => preloadReady())
	.then(() => {

		hydrateRoot(
			rootElement,

			// StrictMode will make the page render twice at start, but only in dev environment
			<StrictMode>
				<SitecoreConfigProvider sitecoreSiteName={siteName}>
					<HelmetProvider>
						<AppRoot
							path={window.location.pathname}
							Router={BrowserRouter}
							ssrState={__JSS_STATE__}
							siteName={siteName}
							routerContext={{}}
						/>
					</HelmetProvider>
				</SitecoreConfigProvider>
			</StrictMode>
		);
	});
